import { useEffect, useState } from "react";
import { Sparkles, Loader2, CheckCircle2, Eye, Lightbulb, RefreshCw, ShieldCheck } from "lucide-react";
import client from "../../lib/api";
import { PageHeader, PrivacyBadge } from "./adminUi";

export default function AdminInsightsScreen() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [aiAvailable, setAiAvailable] = useState(true);

  const load = async () => {
    try {
      const { data } = await client.get("/admin/insights");
      setData(data.insights);
      setAiAvailable(!!data.ai_available);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await client.post("/admin/insights/generate");
      setData(data.insights);
      setAiAvailable(!!data.ai_available);
    } finally { setGenerating(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin" size={20} /></div>;

  const ins = data?.insights;

  return (
    <div data-testid="admin-insights">
      <PageHeader
        title="Klair insights"
        subtitle="Klair reads your aggregate cohort and writes plain-language insights and suggestions. It checks its own draft for privacy and accuracy before showing it."
        actions={
          <>
            <PrivacyBadge />
            <button className="k-btn k-btn-primary !py-2" onClick={generate} disabled={generating} data-testid="insights-generate-btn">
              {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
              {data ? "Regenerate" : "Generate insights"}
            </button>
          </>
        }
      />

      {generating && (
        <div className="k-card mb-6 flex items-center gap-3" style={{ padding: "1rem 1.25rem" }} data-testid="insights-generating">
          <Loader2 size={18} className="animate-spin" style={{ color: "#1D5FD4" }} />
          <span className="text-sm" style={{ color: "#131822" }}>Klair is reading your cohort and refining a draft. This takes a moment.</span>
        </div>
      )}

      {!data && !generating && (
        <div className="k-card flex flex-col items-center text-center" style={{ padding: "3.5rem 2rem" }} data-testid="insights-empty">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(29,95,212,0.1)", color: "#1D5FD4" }}><Sparkles size={26} /></div>
          <h2 className="klareet-serif mb-2" style={{ fontSize: "1.4rem", color: "#131822" }}>No insights yet</h2>
          <p className="k-quiet mb-5" style={{ maxWidth: "32rem", lineHeight: 1.6 }}>Generate a read of your cohort. Klair works only from anonymised group numbers, never an individual.</p>
          <button className="k-btn k-btn-primary" onClick={generate} data-testid="insights-empty-generate">
            <Sparkles size={15} /> Generate insights
          </button>
        </div>
      )}

      {data && ins && (
        <>
          <div className="k-card mb-6 flex items-start gap-3" style={{ padding: "1.25rem 1.5rem" }} data-testid="insights-headline">
            <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#1D5FD4", color: "white" }}><Sparkles size={17} /></span>
            <div>
              <p className="klareet-serif" style={{ fontSize: "1.2rem", color: "#131822", lineHeight: 1.45 }}>{ins.headline}</p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="k-chip !text-xs" data-testid="insights-source">
                  {data.source === "ai" ? "Written by Klair, refined by a self-check" : "Built from your aggregate numbers"}
                </span>
                <span className="k-quiet !text-xs">Across {data.cohort_size} students{data.generated_at ? ` · ${new Date(data.generated_at).toLocaleString()}` : ""}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <InsightColumn testId="insights-working" icon={CheckCircle2} color="#0E7A66" title="What is working" items={ins.whats_working} />
            <InsightColumn testId="insights-watch" icon={Eye} color="#8A5808" title="Worth watching" items={ins.watch_areas} />
            <div className="k-card" style={{ padding: "1.5rem" }} data-testid="insights-suggestions">
              <div className="flex items-center gap-2 text-sm font-medium mb-4" style={{ color: "#1D5FD4" }}><Lightbulb size={16} /> Suggestions</div>
              <div className="space-y-3">
                {(ins.suggestions || []).map((s, i) => (
                  <div key={i} className="rounded-lg p-3" style={{ background: "rgba(19,24,34,0.02)", border: "1px solid rgba(19,24,34,0.06)" }} data-testid={`insights-suggestion-${i}`}>
                    <div className="text-sm font-semibold mb-0.5" style={{ color: "#131822" }}>{s.title}</div>
                    <div className="text-sm" style={{ color: "rgba(19,24,34,0.7)", lineHeight: 1.5 }}>{s.detail}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="k-card mt-6 flex items-start gap-3" style={{ padding: "1rem 1.25rem" }} data-testid="insights-note">
            <ShieldCheck size={16} style={{ color: "#0E7A66", marginTop: 2 }} />
            <p className="text-sm" style={{ color: "rgba(19,24,34,0.7)", lineHeight: 1.5 }}>
              These insights come from aggregate numbers only. They are a development read for your team, not a way to rank or select students.
              {!aiAvailable && " Klair is not connected right now, so this was built directly from your aggregates."}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function InsightColumn({ testId, icon: Icon, color, title, items }) {
  return (
    <div className="k-card" style={{ padding: "1.5rem" }} data-testid={testId}>
      <div className="flex items-center gap-2 text-sm font-medium mb-4" style={{ color }}><Icon size={16} /> {title}</div>
      <ul className="space-y-3">
        {(items || []).map((t, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm" style={{ color: "#131822", lineHeight: 1.5 }} data-testid={`${testId}-item-${i}`}>
            <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: color }} />
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}
